import { useMemo, useRef } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Dropdown } from 'components/Dropdown';
import { StyledSeparator } from 'components/Dropdown/styles';
import { useDropdownPlacement } from 'components/Dropdown/useDropdownPlacement';
import { useContactExportTracking } from 'pages/contacts/ContactItem/CrmExportContainer/useContactExportTracking';
import { translate } from 'services/i18n';

import type { CrmAccount } from '../../../../domain/crm';
import { Crm } from '../../../../domain/crm';
import { useCrmStatusQuery } from '../../../../features/crm/useCrmStatusQuery';
import { useCrmStatus } from '../../../../hooks/useCrmStatus';
import { StyledLoader } from '../../ContactList/styles';
import { NEW_ACCOUNT_VALUE } from '../CrmExportContainer/CrmExportContainer';

import { CrmExportPreviewAccountItem } from './CrmExportPreviewAccountItem';
import {
  StyledCrmExportDropdownWrapper,
  StyledCrmExportPreviewSelect,
  StyledNewAccount,
  StyledAccountCreateOption,
  StyledAccountSection,
  StyledAccountsList,
  StyledExistAsLead,
  StyledCrmExportPreviewSelectArrow,
} from './styles';
import { useCrmExportPreviewAccountSelect } from './useCrmExportPreviewAccountSelect';

export type CrmExportPreviewModalProps = {
  crm: Crm;
  companyName?: string;
  isOpen: boolean;
  selectedAccount: CrmAccount | null;
  setSelectedAccount: (account: CrmAccount) => void;
  matchedCompanies?: CrmAccount[];
  dropdownPlacement?: 'left' | 'right';
  isExistAsLead?: boolean;
};

const DROPDOWN_MAX_HEIGHT = 300;

export const CrmExportPreviewAccountSelect = ({
  crm,
  companyName,
  isOpen,
  selectedAccount,
  setSelectedAccount,
  matchedCompanies = [],
  dropdownPlacement = 'left',
  isExistAsLead = false,
}: CrmExportPreviewModalProps) => {
  const crmStatusQuery = useCrmStatusQuery(crm);
  const canCreateNewAccount = crmStatusQuery.data?.isAccountExportEnabled;
  const triggerRef = useRef<HTMLDivElement>(null);

  const { handleTrackExportEvent } = useContactExportTracking(crm);

  const {
    suggestedAccounts,
    isFetchingNextPage,
    isAllAccountsLoading,
    allAccounts,
    handleCloseDropdown,
    handleSelectAccount,
    isDropdownOpened,
    setIsDropdownOpened,
    searchText,
    handleSearchText,
  } = useCrmExportPreviewAccountSelect({
    crm,
    isOpen,
    setSelectedAccount,
    trackExportEvent: handleTrackExportEvent,
    matchedCompanies,
  });

  const { isSfLabelsEnabled, isHbLabelsEnabled } = useCrmStatus();
  const isCrmLabelsEnabled = crm === Crm.salesforce ? isSfLabelsEnabled : isHbLabelsEnabled;

  const adjustedPlacement = useDropdownPlacement(triggerRef, DROPDOWN_MAX_HEIGHT, dropdownPlacement);

  const isNewAccount = selectedAccount?.id === NEW_ACCOUNT_VALUE;
  const label = useMemo(() => {
    if (isExistAsLead) {
      return '';
    }
    if (isNewAccount) {
      return companyName;
    }
    const placeholder = translate(
      crm === Crm.salesforce
        ? 'contacts.contact.export_to_crm.preview_modal.select_placeholder'
        : 'contacts.contact.export_to_crm.preview_modal.select_placeholder.company'
    );
    return selectedAccount?.name || placeholder;
  }, [selectedAccount, companyName, isExistAsLead]);

  const handleDropdownClick = () => {
    if (!isExistAsLead) {
      setIsDropdownOpened(true);
    }
  };

  return (
    <StyledCrmExportDropdownWrapper data-automation="CrmExportPreviewModalDd" ref={triggerRef}>
      {isNewAccount ? (
        <StyledNewAccount>
          {translate(
            crm === Crm.salesforce
              ? 'contacts.contact.export_to_crm.preview_modal.new_account'
              : 'contacts.contact.export_to_crm.preview_modal.new_company'
          )}
        </StyledNewAccount>
      ) : null}
      {isExistAsLead ? (
        <StyledExistAsLead>{translate('contacts.bulk.export_to_crm.preview_modal.exist_as_lead')}</StyledExistAsLead>
      ) : null}
      <StyledCrmExportPreviewSelect
        onClick={handleDropdownClick}
        isSelected={!!selectedAccount}
        isDisabled={isExistAsLead}
      >
        {label}
        <StyledCrmExportPreviewSelectArrow
          iconName={isExistAsLead ? 'arrow' : 'chev-down'}
          size={isExistAsLead ? '10px' : 'xs'}
          iconColor={isExistAsLead ? colorsPalettes.midnight[100] : colorsPalettes.carbon[500]}
          isExistAsLead={isExistAsLead}
        />
      </StyledCrmExportPreviewSelect>
      <Dropdown
        isOpened={isDropdownOpened}
        onClose={handleCloseDropdown}
        onCloseFinish={() => handleSearchText('')}
        searchProps={{
          placeholder:
            translate(
              crm === Crm.salesforce
                ? 'contacts.contact.export_to_crm.preview_modal.search'
                : 'contacts.contact.export_to_crm.preview_modal.search.company'
            ) || '',
          value: searchText,
          onChange: (e) => handleSearchText(e.target.value),
          isLoading: isAllAccountsLoading,
        }}
        placement={adjustedPlacement}
        maxHeight={DROPDOWN_MAX_HEIGHT}
        minWidth={361}
      >
        <StyledAccountsList data-automation="CrmExportPreviewModalDdList">
          {canCreateNewAccount ? (
            <>
              <StyledAccountCreateOption onClick={() => handleSelectAccount({ id: NEW_ACCOUNT_VALUE })}>
                <SWReactIcons iconName="add" size="sm" />
                {translate(
                  crm === Crm.salesforce
                    ? 'contacts.contact.export_to_crm.preview_modal.new_account.create'
                    : 'contacts.contact.export_to_crm.preview_modal.new_company.create'
                )}
              </StyledAccountCreateOption>
              {suggestedAccounts.length > 0 || allAccounts.length > 0 ? (
                <StyledSeparator color={colorsPalettes.carbon[100]} />
              ) : null}
            </>
          ) : null}
          {suggestedAccounts.length > 0 ? (
            <StyledAccountSection data-automation="CrmExportPreviewModalSuggested">
              <SWReactIcons iconName="category" size="xs" iconColor={colorsPalettes.carbon[200]} />
              {translate(
                crm === Crm.salesforce
                  ? 'contacts.contact.export_to_crm.preview_modal.suggested_accounts'
                  : 'contacts.contact.export_to_crm.preview_modal.suggested_companies'
              )}
            </StyledAccountSection>
          ) : null}
          {suggestedAccounts.map((account) => (
            <CrmExportPreviewAccountItem
              account={account}
              key={account.id}
              handleSelectAccount={handleSelectAccount}
              crm={crm}
              trackExportEvent={handleTrackExportEvent}
              ownerName={isCrmLabelsEnabled ? account.ownerName : undefined}
            />
          ))}
          {allAccounts.length > 0 ? (
            <StyledAccountSection data-automation="CrmExportPreviewModalAll">
              <SWReactIcons iconName="category" size="xs" iconColor={colorsPalettes.carbon[200]} />
              {translate(
                crm === Crm.salesforce
                  ? 'contacts.contact.export_to_crm.preview_modal.all_accounts'
                  : 'contacts.contact.export_to_crm.preview_modal.all_companies'
              )}
            </StyledAccountSection>
          ) : null}
          {allAccounts.map((account) => (
            <CrmExportPreviewAccountItem
              account={account}
              key={account.id}
              handleSelectAccount={handleSelectAccount}
              crm={crm}
              trackExportEvent={handleTrackExportEvent}
              ownerName={isCrmLabelsEnabled ? account.ownerName : undefined}
            />
          ))}
          {isFetchingNextPage ? (
            <StyledAccountSection>
              <StyledLoader />
            </StyledAccountSection>
          ) : null}
        </StyledAccountsList>
      </Dropdown>
    </StyledCrmExportDropdownWrapper>
  );
};
